import { useLocationsJobsSubscriptionCancelSummaryRetrieve } from "@sidegigllc/api/client";
import { JobsTeaserCta } from "domains/hire/components/JobsTeaserCta";
import { JobsGateDialogs } from "domains/hire/containers/JobsGateDialogs";
import { useJobsGateState } from "domains/hire/hooks/useJobsGateState";
import { JOBS_STAFF_APPLICANTS_NOTE, JOBS_TRIAL_DAYS, askAnAdmin, jobsTeaserCopy } from "domains/hire/jobsPlan";
import * as React from "react";

type JobsTeaserCtaContainerProps = {
  locationId: number;
  className?: string;
  /** On a job page the count is that job's applicants, not the location's. */
  onJobPage?: boolean;
  jobApplicants?: number;
  /** On a job page: whether that job is open. A closed job has nothing "live" to announce. */
  jobOpen?: boolean;
};

/** "Start free trial" or "Resubscribe" while the location's applicants are hidden; nothing once it sees them. */
export const JobsTeaserCtaContainer = ({
  locationId,
  className,
  onJobPage,
  jobApplicants,
  jobOpen,
}: JobsTeaserCtaContainerProps) => {
  const { location, status, isStaff, canManage, gate } = useJobsGateState(locationId);
  const locked = status?.access === "TEASER";
  // Archived jobs are no longer open, so the summary cannot count their applicants.
  const archived = status?.state === "ARCHIVED";
  // The plan endpoints refuse team members, and their copy asks an admin instead of quoting the count.
  const { data: summary } = useLocationsJobsSubscriptionCancelSummaryRetrieve(locationId, {
    query: { enabled: canManage && locked && !archived && !onJobPage },
  });
  const [dialog, setDialog] = React.useState<"trial" | "resubscribe" | null>(null);
  const [trialEndsAt] = React.useState(() => new Date(Date.now() + JOBS_TRIAL_DAYS * 24 * 60 * 60 * 1000));

  if (!location || !status || !locked) return null;
  const locationApplicants = archived ? undefined : summary?.applicants;
  const applicants = onJobPage ? jobApplicants : locationApplicants;
  // "Your jobs are live" is only true with an open job; the resubscribe copy stands either way.
  const nothingOpen = onJobPage ? jobOpen === false : summary?.open_jobs === 0;
  if (nothingOpen && status.can_start_trial && !applicants) return null;
  const teaser = jobsTeaserCopy(status, {
    locationName: location.name,
    applicants,
    onJobPage,
    trialEndsAt,
  });
  if (!teaser) return null;
  const copy = gate === "askAdmin" ? askAnAdmin(teaser, location.name) : teaser;

  return (
    <>
      <JobsTeaserCta
        copy={copy}
        className={className}
        note={isStaff ? JOBS_STAFF_APPLICANTS_NOTE : undefined}
        onAction={() => setDialog(copy.action)}
      />
      <JobsGateDialogs locationId={locationId} open={dialog} onClose={() => setDialog(null)} />
    </>
  );
};
